<template>
  <div>
    <v-dialog
      v-model="dialog.show"
      max-width="600px"
      scrollable
      persistent
    >
      <v-card>
        <v-card-title class="text-h5">Disclaimer and Terms & Conditions</v-card-title>
        <v-divider></v-divider>
        <v-card-text class="text--primary px-8">
            <p class="text-title pt-8">Disclaimer (Pre-Booking Consent)</p>
            <p>
              By clicking "I Agree" during the booking process, you confirm that you have read, understood, and accepted Asian Allied Freight Management Corporation's Terms and Conditions.
            </p>

            <p class="text-title pt-4">Terms and Conditions of Claims and Insurance</p>
            <p>
              These Terms and Conditions apply to all shipments handled by Asian Allied Freight Management Corporation (AAF). By booking with us, you agree to be bound by the following terms:
            </p>

            <ol class="level-1 pt-4">
              <li>
                <p class="text-title">Claims and Loss Liability</p>
                <ol class="level-2">
                  <li>AAF offers insurance options to cover lost or missing shipments. Insurance must be purchased prior to shipment dispatch and confirmed by a valid payment.</li>
                  <li>A claim for lost, damaged, or missing items must be filed in writing within 3 calendar days from the delivery date, or from the date the shipment was scheduled to arrive.</li>
                  <li>Claims must be submitted through our official support system (Logistics Consultants) or email with all required documentation.</li>
                  <li>Required documentation includes:
                    <ul class="list-circle">
                      <li>Invoice from the supplier</li>
                      <li>Payment receipt</li>
                      <li>Photos of the package before and after delivery (if available)</li>
                      <li>Written explanation of the issue</li>
                    </ul>
                  </li>
                  <li>AAF will acknowledge receipt of your claim within 3 business days. Investigation may take up to 30 business days.</li>
                  <li>Failure to provide complete or accurate information may result in denial of the claim.</li>
                  <li>If a dispute arises that cannot be resolved through our standard process, it may be escalated to a third-party mediator agreed upon by both parties.</li>
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Insurance Coverage</p>
                <ol class="level-2">
                  <li> Insurance coverage is based solely on the declared value of the cargo submitted at the time of booking.</li>
                  <li>The cost of insurance is determined as a percentage of the declared value and must be paid before the cargo is dispatched.</li>
                  <li>Partial claims may be granted where only part of the shipment is lost or damaged, based on the proportion of value lost.</li>
                  <li>Insurance does not guarantee compensation for profit loss, market value depreciation, or time-sensitive materials.</li>
                  <li>If false or misleading information is provided, AAF reserves the right to void any insurance coverage.</li>
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title mb-3">Exclusions from Coverage</p>
                <p class="subtitle-1 font-weight-bold mb-1">Insurance and liability do not cover:</p>
                <ul class="list-circle">
                  <li>Prohibited or restricted items (as listed in AAF's Prohibited Products section)</li>
                  <li>Natural disasters, force majeure, or events beyond AAF's reasonable control</li>
                  <li>Packages damaged due to insufficient packaging</li>
                  <li>Items confiscated and or damaged by customs or government agencies</li>
                  <li>Consequential losses (e.g., loss of business, revenue, or opportunity)</li>                  
                </ul>
              </li>


              <li class="pt-6">
                <p class="text-title">Responsibilities of the Shipper</p>
                <ol class="level-2">
                  <li>The client is responsible for ensuring that all items are correctly declared and are securely packaged according to international shipping standards.</li>
                  <li>Shipping Marks must be clearly and legibly labeled on all boxes; failure to do so may result in delivery delays or rejection of claims.</li>
                  <li>Claims must include:
                    <ul class="list-circle">
                      <li>Proof of payment or invoice from the supplier</li>
                      <li>Photos of packaging before shipment (if claiming for damage)</li>
                      <li>Tracking number / Shipping Mark</li>
                    </ul>
                  </li>
                  <li>In case of failure to respond to AAF's claim resolution communication within 14 days, the claim will be considered closed.</li>
                  <li>Clients must ensure that no restricted or prohibited items are included in the shipment.</li>                  
                </ol>
              </li>


              <li class="pt-6">
                <p class="text-title">Resolution</p>
                <ol class="level-2">
                  <li>Once an investigation is complete, AAF will notify the client of the result and, if applicable, process a refund or shipment credits within 30 business days or the next client’s shipment.</li>
                  <li>If it is determined that AAF is not at fault, no compensation will be granted.</li>
                  <li>Approved claims under insurance will be reimbursed based on the declared value, not exceeding the insured amount.</li>
                </ol>                
              </li>

              <p class="pt-4">
                These terms are modeled after practices used by leading logistics companies such as FedEx and UPS and are designed to ensure fair handling and transparency in all insurance-related matters. For more information, contact your Logistics Consultant before booking.
              </p>


              <li class="pt-6">
                <p class="text-title">Governing Law and Jurisdiction</p>
                <ol class="level-2">
                  <li>These Terms and Conditions shall be governed by and construed in accordance with the laws of the Republic of the Philippines.</li>
                  <li>Any disputes arising out of or in connection with these Terms shall be submitted to the exclusive jurisdiction of the courts of Malabon City, Philippines.</li>                  
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Force Majeure</p>
                <ol class="level-2">
                  <li>AAF shall not be liable for delays, loss, or damage resulting from events beyond its reasonable control, including but not limited to:
                    <ul class="list-circle">
                      <li>Natural disasters (earthquakes, floods, typhoons)</li>
                      <li>Acts of terrorism, war, or armed conflict</li>
                      <li>Cyberattacks or major system outages</li>
                      <li>Labor strikes or port closures</li>
                      <li>Supply chain disruptions or fuel shortages</li>
                      <li>Customs delays or legal embargoes</li>
                    </ul>
                  </li>
                  <li>In such cases, AAF will make reasonable efforts to notify affected clients and mitigate delays, but no liability shall be assumed.</li>                  
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Severability</p>
                <ol class="level-2">
                  <li>If any provision of these Terms and Conditions is found to be invalid or unenforceable under applicable law, such provision shall be ineffective to the extent of such invalidity only, without affecting the remaining provisions of these Terms, which shall continue in full force and effect.</li>                  
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Right to Amend</p>
                <ol class="level-2">
                  <li>AAF reserves the right to update, amend, or revise these Terms and Conditions at any time without prior notice. It is the responsibility of the client to review these Terms regularly. Continued use of AAF services after any changes constitutes acceptance of those changes.</li>                  
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Definitions</p>
                <ul class="list-circle">
                  <li><strong>"Client"</strong> refers to the individual or entity booking logistics services through AAF.</li>
                  <li><strong>"Shipment"</strong> refers to goods transported using AAF services.</li>
                  <li><strong>"Declared Value"</strong> refers to the monetary value of the cargo as stated by the client during booking.</li>
                  <li><strong>"Cargo"</strong> refers to the contents of the shipment.</li>
                  <li><strong>"Logistics Consultant"</strong> refers to a representative of AAF responsible for assisting clients with freight and delivery matters.</li>                  
                </ul>                
              </li>

              <li class="pt-6">
                <p class="text-title">Right to Refuse Service</p>
                <ol class="level-2">
                  <li>AAF reserves the right to refuse or cancel any shipment at its sole discretion if it is deemed unsafe, unlawful, non-compliant with our policies, or likely to incur risk.</li>
                  <li>Bookings containing suspicious, improperly declared, or high-risk items may be rejected without prior notice.</li>                  
                </ol>
              </li>

              <li class="pt-6">
                <p class="text-title">Data Privacy and Use</p>
                <ol class="level-2">
                  <li>Client data submitted through our platform (including name, contact details, and shipment information) is used solely for the purpose of fulfilling logistics services.</li>
                  <li>AAF ensures all customer data is handled in accordance with applicable privacy laws and will not be shared with third parties except as required by law or for operational fulfillment.</li>
                  <li>Clients may request access to their data or opt to delete their data from our systems by contacting support.</li>                  
                </ol>
              </li>
            </ol>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="text-center py-3">
          <v-spacer></v-spacer>
          <!-- <v-btn
            class="mr-4"
            color="darken-1 px-14"
            depressed
            rounded
            large
            @click="doCancel()"
          >
            Cancel
          </v-btn> -->

          <v-btn
            color="primary darken-1 px-14"
            depressed
            rounded
            large
            @click="doCancel()"
          >
            Close
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  export default {
    name: 'insurance-terms-dialog',
    props: ['dialog'],
    data: () => ({
    }),
    computed: {
    },
    methods: {
      doConfirm (){
        this.$emit('doConfirm')
      },
      doCancel (){
        this.$emit('doCancel')
      },
    },
    mounted() {
    },
    components: {
    }
  }
</script>

<style scoped lang="pcss">
  .text-title {
    font-size: 1.25rem;
    font-weight: 500
  }

  .list-circle {
    list-style-type: disc;
  }

  .level-1 {
    counter-reset: section;
  }

  .level-1 > li {
    counter-increment: section;
  }

  .level-1 > li::marker {
    content: counter(section) ". ";
    font-size: 1.25rem;
    font-weight: 500
  }

  .level-2 {
    counter-reset: subsection;    
  }

  .level-2 > li {
    counter-increment: subsection;
    margin-bottom: 6px;
    padding-left: 6px
  }

  .level-2 > li::marker {
    content: counter(section) "." counter(subsection) " ";
    font-weight: 500
  }
</style>
